// @flow
import FormBuilderSettingsCard from 'pages/Components/FormBuilderSettingsCard'
import FormBuilderSettingsCustomInvitePacketConsumer from 'pages/FormBuilderSettingsCustomInvite/Components/FormBuilderSettingsCustomInvitePacketConsumer'
import * as React from 'react'
import { Link, useParams } from 'react-router-dom'
import Text from 'ui/atoms/Text'

import Loading from '../../components/Loading.jsx'
import { useLoadReminderById } from '../../queries/reminders/useLoadReminderById'
import { useLoadGlobalReminder } from '../../queries/reminders/useLoadReminders'
import { useLoadResourcesSettings } from '../../queries/resources_settings/useLoadResourcesSettings'
import FormBuilderSettingsCustomInviteDocumentConsumer from './Components/FormBuilderSettingsCustomInviteDocumentConsumer'
import FormBuilderSettingsCustomInviteFormConsumer from './Components/FormBuilderSettingsCustomInviteFormConsumer'
import FormControlsSettingsModalForm from './Components/FormControlsSettingsModalForm'

type Props = {
  resourceType?: 'form' | 'document' | 'packet',
}

function FormBuilderSettingsCustomInvite({
  resourceType = 'form',
}: Props): React.Node {
  const params = useParams()

  const resourceId =
    resourceType === 'packet' ? params.packetId : params.resourceId

  const { isLoading } = useLoadResourcesSettings(String(resourceId))

  const reminderByResourceTypeId = { [resourceType]: String(resourceId) }
  const { isLoading: isLoadingReminder } = useLoadReminderById({
    ...reminderByResourceTypeId,
  })

  const { isLoading: isLoadingGlobalReminder } = useLoadGlobalReminder()

  const Consumer = React.useMemo(() => {
    switch (resourceType) {
      case 'document':
        return FormBuilderSettingsCustomInviteDocumentConsumer
      case 'packet':
        return FormBuilderSettingsCustomInvitePacketConsumer
      case 'form':
        return FormBuilderSettingsCustomInviteFormConsumer
      default:
        throw new Error('Invalid resource type')
    }
  }, [resourceType])

  let customDescription =
    'Customize how you invite people to fill out your packet.'
  if (resourceType === 'form') {
    customDescription = 'Customize how you invite people to fill out your form.'
  }
  if (resourceType === 'document') {
    customDescription =
      'Customize how you invite people to fill out your document.'
  }

  return (
    <FormBuilderSettingsCard
      subtitle={<Text $light>{customDescription}</Text>}
      title="Custom Email and SMS Invitation"
    >
      {isLoading && isLoadingGlobalReminder && isLoadingReminder ? (
        <div className="h-fit w-full py-8">
          <Loading style={{ height: '128px' }} />
        </div>
      ) : null}
      {!isLoading && !isLoadingGlobalReminder && !isLoadingReminder ? (
        <Consumer>
          {({ extraContexts, setExtraContexts, setIsDirty }) => (
            <FormControlsSettingsModalForm
              extraContexts={extraContexts}
              key={params.resourceId || params.packetId}
              resourceId={params.resourceId || params.packetId}
              resourceType={resourceType}
              setExtraContexts={(data) => {
                setExtraContexts(data)
                setIsDirty(true)
              }}
            />
          )}
        </Consumer>
      ) : null}

      <div className="mt-4 flex w-full flex-col">
        <Text $light>
          You can edit the global message for all forms and packets in Settings
          Page
        </Text>
        <Link className="text-primary" to="/settings">
          Go to Settings Page to edit the global message →
        </Link>
      </div>
    </FormBuilderSettingsCard>
  )
}

export default FormBuilderSettingsCustomInvite
